import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiUrl } from '../../../../../config/environment';
import { getAuthToken } from '../../../../../core/auth/supabase-auth';
import { EvaluationTablesData, UseEvaluationTablesReturn } from '../types';
import { generateCSVContent, generateTsCSVContent, downloadFile } from '../utils';

// Retry configuration for waiting for backend to save results
const MAX_RETRIES = 10;
const RETRY_DELAY_MS = 2000; // 2 seconds between retries

/**
 * Custom hook for EvaluationTables state management, data fetching, and download
 */
export const useEvaluationTables = (sessionId: string): UseEvaluationTablesReturn => {
  const { t } = useTranslation('training');

  const [dfEval, setDfEval] = useState<Record<string, unknown> | null>(null);
  const [dfEvalTs, setDfEvalTs] = useState<Record<string, unknown> | null>(null);
  const [modelType, setModelType] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Refs for cleanup
  const abortControllerRef = useRef<AbortController | null>(null);
  const saveMessageTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);

  // Fetch evaluation tables from backend API with retry logic
  const fetchEvaluationTables = useCallback(async (isRetry = false) => {
    if (!sessionId) return;

    // Cancel previous request if exists
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort();
      } catch {
        // Ignore abort errors
      }
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    // Only set loading=true on first attempt, not retries
    if (!isRetry) {
      setLoading(true);
      setError(null);
      retryCountRef.current = 0;
    }

    try {
      const token = await getAuthToken();
      if (!token) {
        setError('Authentication required');
        setLoading(false);
        return;
      }

      const response = await fetch(
        getApiUrl(`/api/training/evaluation-tables/${sessionId}`),
        {
          signal,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      // Handle 404 - data not yet in database, retry
      if (response.status === 404 && retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        retryTimeoutRef.current = setTimeout(() => {
          fetchEvaluationTables(true);
        }, RETRY_DELAY_MS);
        return; // Keep loading=true, don't set error
      }

      const data: EvaluationTablesData = await response.json();

      if (data.success && data.df_eval && data.df_eval_ts) {
        setDfEval(data.df_eval);
        setDfEvalTs(data.df_eval_ts);
        setModelType(data.model_type || 'Unknown');
        setError(null);
        setLoading(false); // Success - stop loading
      } else {
        // Handle the case where model hasn't been trained yet
        if (data.error && data.error.includes('No evaluation metrics found')) {
          // If still within retry limit, retry
          if (retryCountRef.current < MAX_RETRIES) {
            retryCountRef.current++;
            retryTimeoutRef.current = setTimeout(() => {
              fetchEvaluationTables(true);
            }, RETRY_DELAY_MS);
            return; // Keep loading=true
          }
          // Retries exhausted
          setError(t('training.evaluation.trainingRequired'));
          setLoading(false);
        } else {
          // Other errors - don't retry
          setError(data.error || t('training.evaluation.fetchError'));
          setLoading(false);
        }
      }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return; // Request was cancelled, not an error
      }
      // On network error, retry if within limit
      if (retryCountRef.current < MAX_RETRIES) {
        retryCountRef.current++;
        retryTimeoutRef.current = setTimeout(() => {
          fetchEvaluationTables(true);
        }, RETRY_DELAY_MS);
        return; // Keep loading=true
      }
      // Retries exhausted
      setError(t('training.evaluation.networkError'));
      setLoading(false);
    }
  }, [sessionId, t]);

  // Fetch on mount and sessionId change
  useEffect(() => {
    fetchEvaluationTables();

    return () => {
      // Cleanup on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      if (saveMessageTimeoutRef.current) {
        clearTimeout(saveMessageTimeoutRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, [fetchEvaluationTables]);

  // Download evaluation tables as CSV (df_eval)
  const downloadEvaluationTables = useCallback(() => {
    if (!dfEval || !dfEvalTs) return;

    const csvContent = generateCSVContent(dfEval, sessionId, modelType);
    const filename = `evaluation_tables_${sessionId}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');

    // Clear any existing timeout
    if (saveMessageTimeoutRef.current) {
      clearTimeout(saveMessageTimeoutRef.current);
    }

    setSaveMessage(t('training.evaluation.downloadSuccess'));
    saveMessageTimeoutRef.current = setTimeout(() => {
      setSaveMessage(null);
      saveMessageTimeoutRef.current = null;
    }, 3000);
  }, [dfEval, dfEvalTs, sessionId, modelType, t]);

  // Download timestep evaluation tables as CSV (df_eval_ts)
  const downloadEvaluationTablesTs = useCallback(() => {
    if (!dfEvalTs) return;

    const csvContent = generateTsCSVContent(dfEvalTs, sessionId, modelType);
    const filename = `evaluation_tables_timestep_${sessionId}_${new Date().toISOString().split('T')[0]}.csv`;
    downloadFile(csvContent, filename, 'text/csv;charset=utf-8;');

    // Clear any existing timeout
    if (saveMessageTimeoutRef.current) {
      clearTimeout(saveMessageTimeoutRef.current);
    }

    setSaveMessage(t('training.evaluation.downloadTsSuccess'));
    saveMessageTimeoutRef.current = setTimeout(() => {
      setSaveMessage(null);
      saveMessageTimeoutRef.current = null;
    }, 3000);
  }, [dfEvalTs, sessionId, modelType, t]);

  return {
    dfEval,
    dfEvalTs,
    modelType,
    loading,
    error,
    saveMessage,
    fetchEvaluationTables,
    downloadEvaluationTables,
    downloadEvaluationTablesTs
  };
};
